import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { faMegaphone } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { SelectionBar } from 'components/UI/V3/SelectionBar/SelectionBar';
import EmptyState from 'components/common/EmptyState';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { NoResults } from '../UI/NoResults/NoResults';
import { buildPath, EmailCampaignDetailPath } from 'routes/Paths';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { CampaignStatusFilter, EmailCampaign } from '../types';
import { StatCard } from '../UI/StatCard/StatCard';
import CampaignsTable from './CampaignsTable';
import CampaignCardList from './CampaignCardList';
import styles from './CampaignsTab.module.scss';

const PAGE_SIZE = 10;

const STATUS_FILTERS: Array<{ id: CampaignStatusFilter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'draft', label: 'Drafts' },
    { id: 'sending', label: 'Sending' },
    { id: 'sent', label: 'Sent' },
    { id: 'failed', label: 'Failed' }
];

export const CampaignsTab: React.FC = () => {
    const history = useHistory();
    const isMobile = useMediaQuery({ maxWidth: 600 });
    const { campaigns, loadingCampaigns, kpiCards, scopeIds, isAllScope, openNewCampaign, openDraft } =
        useEmailMarketing();

    const [status, setStatus] = useState<CampaignStatusFilter>('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();

        return campaigns.filter(campaign => {
            if (status !== 'all' && campaign.status !== status) return false;
            if (!isAllScope && !campaign.locationIds.some(id => scopeIds.includes(id))) return false;
            if (term && !`${campaign.name} ${campaign.subject}`.toLowerCase().includes(term)) return false;
            return true;
        });
    }, [campaigns, status, scopeIds, isAllScope, search]);

    useEffect(() => setPage(1), [status, scopeIds, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleOpen = (campaign: EmailCampaign) => {
        if (campaign.status === 'draft') {
            openDraft(campaign);
            return;
        }
        history.push(buildPath(EmailCampaignDetailPath, { id: campaign.id }));
    };

    if (loadingCampaigns) {
        return (
            <div className={styles.emptyWrapper}>
                <Spinner />
            </div>
        );
    }

    if (campaigns.length === 0) {
        return (
            <div className={styles.emptyWrapper}>
                <EmptyState
                    icon={faMegaphone}
                    title="Send your first campaign"
                    description="Reach customers across all your locations with one email. Pick the locations, choose a template and send."
                    showButton
                    buttonLabel="New campaign"
                    buttonIcon={faPlus}
                    onButtonClick={() => openNewCampaign()}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.kpis}>
                {kpiCards.map(kpi => (
                    <StatCard
                        key={kpi.id}
                        label={kpi.label}
                        value={kpi.value}
                        hint={kpi.hint}
                        delta={kpi.delta}
                    />
                ))}
            </div>

            <div className={styles.toolbar}>
                {/* Same control as the Dashboard tabs (Sales / Bookings / Clients / Staff). */}
                <SelectionBar
                    options={STATUS_FILTERS.map(option => ({ id: option.id, name: option.label }))}
                    selectedId={status}
                    background="#f8f8f8"
                    className={styles.statusBar}
                    onSelect={id => setStatus(id as CampaignStatusFilter)}
                />

                <SearchBar
                    searchText={search}
                    placeholder="Search by campaign name or subject"
                    onChangeSearchText={setSearch}
                />
            </div>

            {filtered.length === 0 ? (
                <div className={styles.noResults}>
                    <NoResults
                        title="No campaigns found"
                        description={['Try another status, location or search term.']}
                    />
                </div>
            ) : isMobile ? (
                <CampaignCardList
                    campaigns={pageItems}
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onOpen={handleOpen}
                />
            ) : (
                <CampaignsTable
                    campaigns={pageItems}
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onOpen={handleOpen}
                />
            )}
        </div>
    );
};

export default CampaignsTab;
